<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { __ } from '@/js/stores/expressions';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import InputLabel from '@/js/components/InputLabel.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import {
        useQueryGetMigratedTenants,
        useQueryGetUnmigratedExternalSystems,
        useQueryGetMigratedExternalSystems,
        useMutationMigrateSystem,
        useQueryGetMigrateSystemConfigOptions,
        useQueryGetKeyIndicateSyncDataTypes,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import {
        isValidDaysBack,
        type KeyIndicateSyncSetting,
    } from '@/js/services/api/backOffice/keyIndicateSyncSettingsApi';
    import FormGroup from '@/js/components/FormGroup.vue';
    import SyncConfigOptions from '@/js/back-office/components/SyncConfigOptions.vue';
    import SyncDataTypeSelection from '@/js/back-office/components/SyncDataTypeSelection.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import { CheckIcon, XIcon } from '@lucide/vue';

    const CREATE_NEW = 'create-new';

    const router = useRouter();
    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    onMounted(() => {
        setFocus('#back-office-migrate-company-select');
    });

    const { data: tenantsData } = useQueryGetMigratedTenants();
    const { data: externalSystemsData } = useQueryGetUnmigratedExternalSystems();
    const { data: migratedExternalSystemsData } = useQueryGetMigratedExternalSystems();
    const { mutateAsync: migrateSystemMutation, isPending } = useMutationMigrateSystem();

    const tenants = computed(() => tenantsData.value ?? []);

    const selectedTenantId = ref('');
    const selectedExternalSystemId = ref<number | ''>('');
    const selectedMigratedExternalSystemId = ref('');
    const systemName = ref('');
    const selectedConfigs = ref<string[]>([]);
    const active = ref(true);

    const isCreateNew = computed(() => selectedMigratedExternalSystemId.value === CREATE_NEW);

    const { data: configOptionsData } = useQueryGetMigrateSystemConfigOptions(selectedTenantId);
    const configOptions = computed(() => configOptionsData.value ?? []);

    watch(configOptionsData, (newOptions) => {
        if (newOptions) {
            selectedConfigs.value = newOptions.filter((o) => o.isSelected).map((o) => o.name);
        }
    });

    const { data: dataTypesData } = useQueryGetKeyIndicateSyncDataTypes();
    const dataTypes = computed(() => dataTypesData.value ?? []);
    const dataTypeSettings = ref<KeyIndicateSyncSetting[]>([]);

    const resetDataTypeSettings = () => {
        dataTypeSettings.value = dataTypes.value.map((t) => ({
            dataType: t.dataType,
            isEnabled: true,
            daysBack: t.defaultDaysBack,
        }));
    };

    // Prefills only once, so a refetch doesn't undo the user's choices.
    watch(
        dataTypes,
        () => {
            if (dataTypeSettings.value.length === 0) {
                resetDataTypeSettings();
            }
        },
        { immediate: true }
    );

    const clearForm = () => {
        selectedExternalSystemId.value = '';
        selectedMigratedExternalSystemId.value = '';
        systemName.value = '';
        selectedConfigs.value = [];
        resetDataTypeSettings();
        active.value = true;
        externalSystemSearch.value = null;
        migratedSystemSearch.value = null;
    };

    watch(selectedTenantId, () => {
        clearForm();
    });

    const selectedCompanyId = computed(() => {
        const tenant = tenants.value.find((t) => t.id === selectedTenantId.value);
        return tenant?.companyId ?? null;
    });

    const externalSystems = computed(() => {
        if (selectedCompanyId.value === null) return [];

        return (externalSystemsData.value ?? []).filter(
            (system) => system.companyId === selectedCompanyId.value
        );
    });

    const allSystemsMigrated = computed(
        () =>
            !!selectedTenantId.value &&
            externalSystemsData.value !== undefined &&
            externalSystemOptions.value.length === 0
    );

    const migratedExternalSystems = computed(() => {
        return migratedExternalSystemsData.value ?? [];
    });

    const tenantSearch = ref<string | null>(null);
    const externalSystemSearch = ref<string | null>(null);
    const migratedSystemSearch = ref<string | null>(null);

    const tenantOptions = computed<Option[]>(() =>
        tenants.value.map((t) => ({ name: t.name, value: t.id }))
    );

    const externalSystemOptions = computed<Option[]>(() =>
        externalSystems.value.map((s) => ({
            name: s.systemName || __('back-office-integration-unnamed'),
            value: String(s.systemId),
        }))
    );

    const migratedSystemOptions = computed<Option[]>(() => [
        { name: __('back-office-install-integration-create-new'), value: CREATE_NEW },
        ...migratedExternalSystems.value.map((s) => ({
            name: s.systemName || __('back-office-integration-unnamed'),
            value: s.systemId,
        })),
    ]);

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };

    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    const onExternalSystemSelect = (option: Option) => {
        selectedExternalSystemId.value = Number(option.value);
        selectedMigratedExternalSystemId.value = '';
        migratedSystemSearch.value = null;
        systemName.value = '';
    };

    const onExternalSystemClear = () => {
        selectedExternalSystemId.value = '';
        selectedMigratedExternalSystemId.value = '';
        systemName.value = '';
    };

    const onMigratedSystemSelect = (option: Option) => {
        selectedMigratedExternalSystemId.value = option.value;
    };

    const onMigratedSystemClear = () => {
        selectedMigratedExternalSystemId.value = '';
        systemName.value = '';
    };

    const canSubmit = computed(() => {
        if (selectedTenantId.value === '' || selectedExternalSystemId.value === '') return false;
        if (selectedMigratedExternalSystemId.value === '') return false;
        if (isCreateNew.value && !systemName.value.trim()) return false;
        const enabledSettings = dataTypeSettings.value.filter((s) => s.isEnabled);
        if (enabledSettings.length === 0) {
            return false;
        }
        if (!enabledSettings.every((s) => isValidDaysBack(s.daysBack))) {
            return false;
        }
        return true;
    });

    const submit = async () => {
        if (!canSubmit.value) return;

        await migrateSystemMutation({
            tenantId: selectedTenantId.value,
            keyIndicateSystemId: selectedExternalSystemId.value as number,
            indicateSystemId: isCreateNew.value ? null : selectedMigratedExternalSystemId.value,
            systemName: isCreateNew.value
                ? systemName.value.trim()
                : (migratedExternalSystems.value.find(
                      (s) => s.systemId === selectedMigratedExternalSystemId.value
                  )?.systemName ?? ''),
            configs: selectedConfigs.value.length > 0 ? selectedConfigs.value : undefined,
            active: active.value,
            dataTypes: dataTypeSettings.value
                .filter((s) => s.isEnabled)
                .map((s) => ({ dataType: s.dataType, daysBack: s.daysBack })),
        });

        clearForm();

        successDialogStore.openDialog({
            message: __('back-office-install-integration-success'),
        });
    };

    const cancel = () => {
        router.push('/back-office/overview');
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">
                {{ __('back-office-install-integration-from-v1') }}
            </h1>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="!canSubmit"
                :loading="isPending"
                @click="submit"
            >
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="cancel">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="back-office-migrate-company-select"
                        :label="__('back-office-install-integration-select-company')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                    <template v-if="selectedTenantId">
                        <div>
                            <AutoComplete
                                v-model="externalSystemSearch"
                                input-id="back-office-migrate-external-system-select"
                                :label="
                                    __('back-office-install-integration-select-external-system')
                                "
                                :options="externalSystemOptions"
                                :disabled="allSystemsMigrated"
                                @select="onExternalSystemSelect"
                                @clear="onExternalSystemClear"
                            />
                            <p v-if="allSystemsMigrated" class="text-caption mt-1 text-gray-500">
                                {{ __('back-office-all-systems-migrated') }}
                            </p>
                        </div>
                        <AutoComplete
                            v-if="selectedExternalSystemId"
                            v-model="migratedSystemSearch"
                            input-id="back-office-migrate-migrated-external-system-select"
                            :label="
                                __(
                                    'back-office-install-integration-select-migrated-external-system'
                                )
                            "
                            :options="migratedSystemOptions"
                            @select="onMigratedSystemSelect"
                            @clear="onMigratedSystemClear"
                        />
                        <div v-if="isCreateNew">
                            <InputLabel for="back-office-migrate-system-name">
                                {{ __('back-office-install-integration-system-name') }}
                            </InputLabel>
                            <TextInput
                                id="back-office-migrate-system-name"
                                v-model="systemName"
                                :width="TextInputWidth.Full"
                            />
                        </div>
                        <SyncConfigOptions
                            v-if="selectedExternalSystemId"
                            v-model="selectedConfigs"
                            :options="configOptions"
                        />
                        <SyncDataTypeSelection
                            v-if="selectedExternalSystemId"
                            v-model="dataTypeSettings"
                            :data-types="dataTypes"
                            id-prefix="back-office-migrate-system"
                        />
                        <template v-if="selectedExternalSystemId">
                            <EchoInfoBox size="small">
                                {{ __('back-office-install-integration-active-help') }}
                            </EchoInfoBox>
                            <CheckboxInput id="back-office-migrate-system-active" v-model="active">
                                {{ __('back-office-install-integration-active') }}
                            </CheckboxInput>
                        </template>
                    </template>
                </FormGroup>
            </form>
        </template>
    </SettingsPageLayout>
</template>
